import { useEffect, useState } from "react";
import "./HustleEatsPage.css";
import "./HustleEatsAdminPage.css";
import StripeConnectPanel from "../components/StripeConnectPanel";
import "../components/StripeConnectPanel.css";
import TablesTab from "../components/phase2/TablesTab";
import FloorPlanTab from "../components/phase2/FloorPlanTab";
import DepositsTab from "../components/phase2/DepositsTab";
import EventsTab from "../components/phase2/EventsTab";
import InterceptTab from "../components/phase2/InterceptTab";
import "../components/phase2/phase2.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* Restaurant-side dashboard for HustleEats Phase 1.
   - Post a new "fill the seat" offer
   - See live offers + claim count
   - Redeem a 6-letter code when a diner walks in
   Auth: standard /admin login required (uses session cookie).
*/

const DEAL_TYPES = [
  ["early_bird", "Early sitting"],
  ["peak", "Prime time"],
  ["late_dining", "Late dining"],
  ["event_day", "Event day"],
];

const WEEKDAYS = [
  [1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"],
  [5, "Fri"], [6, "Sat"], [7, "Sun"],
];

function todayPlusDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 16);
}

function moneyFromCents(c) {
  return ((c || 0) / 100).toFixed(2);
}

export default function HustleEatsAdminPage() {
  const [venueSlug, setVenueSlug] = useState("osteria-epoca");
  const [regionSlug, setRegionSlug] = useState("brisbane-south-yeronga");
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemMsg, setRedeemMsg] = useState(null);
  const [form, setForm] = useState({
    title: "",
    blurb: "",
    deal_type: "early_bird",
    credit_amount_cents: 1000,
    valid_from: todayPlusDays(0),
    valid_until: todayPlusDays(30),
    days_of_week: [],
    time_start: "17:00",
    time_end: "18:30",
    max_covers: 10,
    hero_image_url: "",
  });
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/hustleeats/offers?venue_slug=${venueSlug}&limit=50`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setOffers(list);
      // Pull stats in parallel.
      const statResults = await Promise.all(
        list.map(async (o) => {
          const r = await fetch(`${API}/api/hustleeats/offers/${o.id}/stats`);
          if (!r.ok) return null;
          const s = await r.json();
          return [o.id, s];
        })
      );
      setStats(Object.fromEntries(statResults.filter(Boolean)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, [venueSlug]);

  const toggleDay = (d) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort(),
    }));
  };

  const createOffer = async (e) => {
    e.preventDefault();
    setCreating(true);
    setCreateMsg(null);
    try {
      const body = {
        venue_slug: venueSlug,
        region_slug: regionSlug,
        state_code: "QLD",
        title: form.title,
        blurb: form.blurb,
        deal_type: form.deal_type,
        credit_amount_cents: Number(form.credit_amount_cents),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString(),
        days_of_week: form.days_of_week,
        time_start: form.time_start || null,
        time_end: form.time_end || null,
        max_covers: Number(form.max_covers),
        hero_image_url: form.hero_image_url || null,
      };
      const res = await fetch(`${API}/api/hustleeats/offers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        setCreateMsg({ kind: "err", text: "Please sign in as a restaurant admin first." });
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setCreateMsg({ kind: "err", text: data.detail || "Couldn't create the offer." });
        return;
      }
      setCreateMsg({ kind: "ok", text: `Offer "${data.title}" is live.` });
      setForm((f) => ({ ...f, title: "", blurb: "" }));
      fetchOffers();
    } finally {
      setCreating(false);
    }
  };

  const redeem = async (e) => {
    e.preventDefault();
    setRedeemMsg(null);
    if (!redeemCode || redeemCode.length !== 6) {
      setRedeemMsg({ kind: "err", text: "Codes are 6 characters." });
      return;
    }
    const res = await fetch(`${API}/api/hustleeats/claims/redeem`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: redeemCode.toUpperCase(),
        venue_slug: venueSlug,
      }),
    });
    if (res.status === 401) {
      setRedeemMsg({ kind: "err", text: "Sign in as restaurant admin to redeem codes." });
      return;
    }
    const data = await res.json();
    if (!res.ok) {
      setRedeemMsg({ kind: "err", text: data.detail || "Couldn't redeem that code." });
      return;
    }
    setRedeemMsg({
      kind: "ok",
      text: `Redeemed. $${moneyFromCents(data.credit_amount_cents)} credit added to diner's wallet, valid 90 days at this venue.`,
    });
    setRedeemCode("");
    fetchOffers();
  };

  const [activeTab, setActiveTab] = useState("offers");
  const TABS = [
    ["offers", "Offers + redeem"],
    ["tables", "Tables"],
    ["floor", "Floor + intel"],
    ["deposits", "Deposits"],
    ["events", "Events"],
    ["intercept", "Bus/train intercept"],
  ];

  return (
    <div className="he-page he-admin">
      <header className="he-admin__head">
        <div>
          <div className="he-eyebrow he-eyebrow--ink">HustleEats · Restaurant dashboard</div>
          <h1 className="he-h1 he-h1--ink">Fill the empty seats.</h1>
          <p className="he-admin__sub">
            Anchor venue: <strong>{venueSlug}</strong>. Post a &ldquo;fill the seat&rdquo; offer below,
            redeem walk-in codes on the right.
          </p>
        </div>
        <div className="he-admin__venue">
          <label>
            Venue slug
            <input
              type="text"
              value={venueSlug}
              onChange={(e) => setVenueSlug(e.target.value.trim().toLowerCase())}
              data-testid="he-admin-venue-input"
            />
          </label>
        </div>
      </header>

      <main className="he-admin__main">
        {/* ── Stripe Connect onboarding (spans full width) ────── */}
        <StripeConnectPanel venueSlug={venueSlug} />
      </main>

      <nav className="ph2-tabs" data-testid="ph2-tabs">
        {TABS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`ph2-tab ${activeTab === k ? "is-active" : ""}`}
            onClick={() => setActiveTab(k)}
            data-testid={`ph2-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </nav>

      {activeTab === "tables" && <TablesTab venueSlug={venueSlug} />}
      {activeTab === "floor" && <FloorPlanTab venueSlug={venueSlug} />}
      {activeTab === "deposits" && <DepositsTab venueSlug={venueSlug} />}
      {activeTab === "events" && <EventsTab venueSlug={venueSlug} />}
      {activeTab === "intercept" && <InterceptTab venueSlug={venueSlug} />}

      {activeTab === "offers" && (
      <>
      <main className="he-admin__main" style={{ marginTop: 0 }}>

        {/* ── Create offer ─────────────────────────────────────── */}
        <section className="he-admin__panel">
          <h2 className="he-admin__h2">New offer</h2>
          <form onSubmit={createOffer} className="he-form">
            <label className="he-form__field">
              <span>Title</span>
              <input
                required minLength={2} maxLength={80}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Tuesday Early Sitting"
                data-testid="he-admin-title"
              />
            </label>
            <label className="he-form__field">
              <span>Blurb</span>
              <textarea
                maxLength={240} rows={2}
                value={form.blurb}
                onChange={(e) => setForm({ ...form, blurb: e.target.value })}
                placeholder="One sentence that sells the seat."
                data-testid="he-admin-blurb"
              />
            </label>
            <div className="he-form__row">
              <label className="he-form__field">
                <span>Deal type</span>
                <select
                  value={form.deal_type}
                  onChange={(e) => setForm({ ...form, deal_type: e.target.value })}
                  data-testid="he-admin-dealtype"
                >
                  {DEAL_TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                </select>
              </label>
              <label className="he-form__field">
                <span>Credit per seat ($)</span>
                <input
                  type="number" min={1} max={100}
                  value={form.credit_amount_cents / 100}
                  onChange={(e) => setForm({ ...form, credit_amount_cents: Number(e.target.value) * 100 })}
                  data-testid="he-admin-credit"
                />
              </label>
              <label className="he-form__field">
                <span>Max covers</span>
                <input
                  type="number" min={1} max={500}
                  value={form.max_covers}
                  onChange={(e) => setForm({ ...form, max_covers: e.target.value })}
                  data-testid="he-admin-maxcovers"
                />
              </label>
            </div>
            <div className="he-form__row">
              <label className="he-form__field">
                <span>Valid from</span>
                <input
                  type="datetime-local"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                />
              </label>
              <label className="he-form__field">
                <span>Valid until</span>
                <input
                  type="datetime-local"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                />
              </label>
            </div>
            <div className="he-form__row">
              <label className="he-form__field">
                <span>Time window start</span>
                <input
                  type="time"
                  value={form.time_start}
                  onChange={(e) => setForm({ ...form, time_start: e.target.value })}
                />
              </label>
              <label className="he-form__field">
                <span>Time window end</span>
                <input
                  type="time"
                  value={form.time_end}
                  onChange={(e) => setForm({ ...form, time_end: e.target.value })}
                />
              </label>
            </div>
            <fieldset className="he-form__field">
              <span>Days of week (leave blank for any day)</span>
              <div className="he-daygrid">
                {WEEKDAYS.map(([d, label]) => (
                  <label key={d} className={`he-day ${form.days_of_week.includes(d) ? "is-on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={form.days_of_week.includes(d)}
                      onChange={() => toggleDay(d)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="he-form__field">
              <span>Hero image URL (optional)</span>
              <input
                type="url"
                value={form.hero_image_url}
                onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                placeholder="https://…"
              />
            </label>
            <button
              type="submit"
              className="he-card__cta he-form__submit"
              disabled={creating}
              data-testid="he-admin-create-btn"
            >
              {creating ? "Posting…" : "Post offer"}
            </button>
            {createMsg && (
              <div className={`he-admin__msg he-admin__msg--${createMsg.kind}`} data-kind={createMsg.kind} data-testid="he-admin-create-msg">
                {createMsg.text}
              </div>
            )}
          </form>
        </section>

        {/* ── Redeem code ──────────────────────────────────────── */}
        <section className="he-admin__panel">
          <h2 className="he-admin__h2">Redeem a walk-in code</h2>
          <p className="he-admin__hint">
            Diner shows up. They give you a 6-letter code. Punch it in here — the seat is marked
            redeemed and a thank-you credit goes into their wallet for next visit.
          </p>
          <form onSubmit={redeem} className="he-form">
            <label className="he-form__field">
              <span>6-letter code</span>
              <input
                type="text"
                maxLength={6}
                value={redeemCode}
                onChange={(e) => setRedeemCode(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ""))}
                placeholder="e.g. K2P7QM"
                data-testid="he-admin-redeem-code"
                style={{ fontFamily: "monospace", letterSpacing: "0.2em", fontSize: 22, textAlign: "center" }}
              />
            </label>
            <button
              type="submit"
              className="he-card__cta he-form__submit"
              data-testid="he-admin-redeem-btn"
            >
              Redeem
            </button>
            {redeemMsg && (
              <div className={`he-admin__msg he-admin__msg--${redeemMsg.kind}`} data-kind={redeemMsg.kind} data-testid="he-admin-redeem-msg">
                {redeemMsg.text}
              </div>
            )}
          </form>
        </section>
      </main>

      {/* ── Live offers ───────────────────────────────────────── */}
      <section className="he-admin__board" data-testid="he-admin-board">
        <h2 className="he-admin__h2">Live offers at {venueSlug}</h2>
        {loading && <div className="he-state">Loading…</div>}
        {!loading && offers.length === 0 && (
          <div className="he-state" data-testid="he-admin-empty">No offers yet — post one above.</div>
        )}
        <div className="he-admin__table" role="table">
          {offers.map((o) => {
            const s = stats[o.id] || {};
            return (
              <div className="he-admin__row" key={o.id} data-testid={`he-admin-row-${o.id}`}>
                <div>
                  <div className="he-admin__row-title">{o.title}</div>
                  <div className="he-admin__row-sub">{o.blurb}</div>
                </div>
                <div className="he-admin__stat">
                  <span>Credit</span>
                  <strong>${moneyFromCents(o.credit_amount_cents)}</strong>
                </div>
                <div className="he-admin__stat">
                  <span>Seats</span>
                  <strong>{s.claimed_covers ?? o.claimed_covers ?? 0}/{o.max_covers}</strong>
                </div>
                <div className="he-admin__stat">
                  <span>Pending</span>
                  <strong>{s.claims_pending ?? "–"}</strong>
                </div>
                <div className="he-admin__stat">
                  <span>Redeemed</span>
                  <strong>{s.claims_redeemed ?? "–"}</strong>
                </div>
                <div className={`he-chip he-chip--${o.status === "live" ? "deal" : "soldout"}`}>{o.status}</div>
              </div>
            );
          })}
        </div>
      </section>
      </>
      )}
    </div>
  );
}
